import * as THREE from 'three';
import { DRIFT_RESUME_SEC } from '../../constants';
import { applyIdleDrift, idleEaseGain } from './idleDrift';

const _zoomDir = new THREE.Vector3();
const _zoomBefore = new THREE.Vector3();
const _zoomAfter = new THREE.Vector3();
const _zoomNdc = new THREE.Vector2();
const _zoomRay = new THREE.Raycaster();
const _zoomPlane = new THREE.Plane();

function worldOnViewPlane(camera, target, ndcX, ndcY, out) {
  _zoomNdc.set(ndcX, ndcY);
  _zoomRay.setFromCamera(_zoomNdc, camera);
  camera.getWorldDirection(_zoomDir);
  _zoomPlane.setFromNormalAndCoplanarPoint(_zoomDir, target);
  if (!_zoomRay.ray.intersectPlane(_zoomPlane, out)) {
    out.copy(target);
    return false;
  }
  return true;
}

export function stepCamera({
  camera,
  controls,
  delta,
  now,
  reducedMotion,
  targetDistRef,
  focusTargetGoalRef,
  focusCameraGoalRef,
  driftRef,
  interactionRef,
  FOCUS_TARGET_LERP,
  FOCUS_CAM_LERP,
  FOCUS_SNAP_EPS,
  ZOOM_DAMPING,
  zoomCursorRef,
  zoomDriveRef,
  MIN_DIST,
  MAX_DIST,
  ZOOM_FRICTION = 7.2,
}) {
  let targetDist = targetDistRef.v;
  let focusTargetGoal = focusTargetGoalRef.v;
  let focusCameraGoal = focusCameraGoalRef.v;
  const isFraming = !!(focusTargetGoal && focusCameraGoal);

  if (isFraming) {
    if (zoomDriveRef) zoomDriveRef.vel = 0;
    controls.target.lerp(focusTargetGoal, 1 - Math.pow(1 - FOCUS_TARGET_LERP, delta * 60));
    camera.position.lerp(focusCameraGoal, 1 - Math.pow(1 - FOCUS_CAM_LERP, delta * 60));
    if (
      controls.target.distanceTo(focusTargetGoal) < FOCUS_SNAP_EPS
      && camera.position.distanceTo(focusCameraGoal) < FOCUS_SNAP_EPS * 3
    ) {
      controls.target.copy(focusTargetGoal);
      camera.position.copy(focusCameraGoal);
      focusTargetGoal = null;
      focusCameraGoal = null;
      targetDist = camera.position.distanceTo(controls.target);
      if (driftRef) driftRef.fresh = true;
      if (interactionRef) interactionRef.lastAt = now;
    }
  } else {
    let vel = zoomDriveRef?.vel ?? 0;
    if (Math.abs(vel) > 0.01) {
      const minD = MIN_DIST ?? 0.2;
      const maxD = MAX_DIST ?? 400;
      targetDist = Math.min(maxD, Math.max(minD, targetDist * Math.exp(vel * delta)));
      vel *= Math.exp(-(ZOOM_FRICTION) * delta);
      if (Math.abs(vel) < 0.01) vel = 0;
      if (zoomDriveRef) zoomDriveRef.vel = vel;
    } else if (zoomDriveRef) {
      zoomDriveRef.vel = 0;
    }
    const curDist = camera.position.distanceTo(controls.target);
    const coasting = Math.abs(zoomDriveRef?.vel ?? 0) > 0.01;
    if (Math.abs(curDist - targetDist) > 0.01 || coasting) {
      const pivot = zoomCursorRef?.live;
      if (pivot) {
        if (!zoomCursorRef.hasPivot) {
          worldOnViewPlane(camera, controls.target, zoomCursorRef.x, zoomCursorRef.y, _zoomBefore);
          zoomCursorRef.px = _zoomBefore.x;
          zoomCursorRef.py = _zoomBefore.y;
          zoomCursorRef.pz = _zoomBefore.z;
          zoomCursorRef.hasPivot = true;
        } else {
          _zoomBefore.set(zoomCursorRef.px, zoomCursorRef.py, zoomCursorRef.pz);
        }
      }
      const zoomT = 1 - Math.pow(1 - ZOOM_DAMPING, delta * 60);
      const newDist = curDist + (targetDist - curDist) * zoomT;
      _zoomDir.copy(camera.position).sub(controls.target).normalize();
      camera.position.copy(controls.target).addScaledVector(_zoomDir, newDist);
      camera.updateMatrixWorld(true);
      if (pivot) {
        worldOnViewPlane(camera, _zoomBefore, zoomCursorRef.x, zoomCursorRef.y, _zoomAfter);
        _zoomDir.copy(_zoomBefore).sub(_zoomAfter);
        camera.position.add(_zoomDir);
        controls.target.add(_zoomDir);
        camera.updateMatrixWorld(true);
      }
    } else if (zoomCursorRef) {
      zoomCursorRef.live = false;
      zoomCursorRef.hasPivot = false;
    }
  }

  focusTargetGoalRef.v = focusTargetGoal;
  focusCameraGoalRef.v = focusCameraGoal;
  targetDistRef.v = targetDist;

  const zoomingNow = !isFraming && (
    zoomCursorRef?.live
    || Math.abs(zoomDriveRef?.vel ?? 0) > 0.01
    || Math.abs(camera.position.distanceTo(controls.target) - targetDist) > 0.02
  );
  if (zoomingNow) camera.lookAt(controls.target);
  else controls.update(delta);

  const veilOpen = !!document.querySelector('.mission-full-view--open:not(.mission-full-view--doc-editing)');
  const driftOn = !reducedMotion && !isFraming && !zoomingNow && (veilOpen || !interactionRef?.dragging);
  if (driftOn && driftRef) {
    const gain = idleEaseGain((now - (interactionRef?.lastAt ?? 0)) / 1000, DRIFT_RESUME_SEC);
    applyIdleDrift(camera, controls, driftRef, delta, gain);
  } else if (driftRef && !zoomingNow) {
    driftRef.fresh = true;
  }

  camera.updateMatrixWorld(true);
  return { isFraming, drifting: !!(driftOn && driftRef), zooming: zoomingNow };
}
